import React, { useState } from 'react';
import { Dialog, DialogContent, DialogTitle, IconButton, Typography } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import { HighlightOffRounded } from '@material-ui/icons';
import Description from '@material-ui/icons/Description';
import AttachFile from '@material-ui/icons/AttachFile';
import { makeStyles } from '@material-ui/core/styles';
import { FileNode } from '../../../api-client/autogenerated';
import AssociateDocumentDialog from '../../add-associations/AssociateDocumentDialog';
import AddDesignFileDialog from '../../dialogs/AddDesignFileDialog';
import { DocumentFileTree, getDocumentTreesForAssociations } from '../../../scripts/utils';
import { useSelector } from 'react-redux';
import { getProjectState } from '../../../features/project/selectors';

const useStyles = makeStyles(() => ({
  titleContainer: {
    display: 'flex',
    flexDirection: 'column',
    padding: '8px 16px',
    borderBottom: '1px solid #EDECEC',
  },
  title: {
    marginTop: 3,
    marginLeft: 6,
    fontWeight: 500,
    fontSize: 22,
    lineHeight: '30px',
    textAlign: 'left',
    color: '#0947B9',
    textTransform: 'uppercase',
  },
  closeButton: {
    padding: 0,
    right: 16,
    top: 12,
    position: 'absolute',
    '&:hover': { backgroundColor: 'transparent' },
  },
}));

export type ProjectFilePathSelection = {
  /** What the field shows -- the document's name, or the file's name. */
  label: string;
  documentId?: string;
  /** The document's template type, which its page's URL is built from. */
  documentType?: string;
  fileNodeKey?: string;
  fileNodeOwnerId?: string;
};

/**
 * Asks which of the two sources a Project File Paths field should point at, then opens that
 * source's own browser -- the same two dialogs the Share Anything card's attach buttons open.
 *
 * One selection either way: the field holds a single path.
 */
export default function BrowseCenterlineFieldDialog({
  open,
  handleClose,
  onSelect,
}: {
  open: boolean;
  handleClose: () => void;
  onSelect: (selection: ProjectFilePathSelection) => void;
}) {
  const classes = useStyles();
  const project = useSelector(getProjectState);

  const [documentDialogOpen, setDocumentDialogOpen] = useState(false);
  const [fileDialogOpen, setFileDialogOpen] = useState(false);
  const [documentTrees, setDocumentTrees] = useState<DocumentFileTree[]>([]);
  const [isLoadingTrees, setIsLoadingTrees] = useState(false);

  // Fetched when the documents browser is asked for rather than with the field, so a form full of
  // these fields does not load the project's documents several times over on render.
  const openDocumentDialog = () => {
    if (!project?.id) return;
    setDocumentDialogOpen(true);
    if (documentTrees.length > 0) return;
    setIsLoadingTrees(true);
    // Every phase, as the Share Anything card does: the dialog groups by phase, and the design
    // and bidding types are excluded by default, so without this their headings would sit here
    // permanently empty. A path may point at any of the project's documents.
    getDocumentTreesForAssociations(project.id, undefined, undefined, true)
      .then(setDocumentTrees)
      .finally(() => setIsLoadingTrees(false));
  };

  /** Done in the documents browser: take whichever document is checked, if any. */
  const takeCheckedDocument = () => {
    const checkedTree = documentTrees.find((tree) =>
      tree.documents.some((document) => document.checked),
    );
    const checked = checkedTree?.documents.find((document) => document.checked);
    setDocumentDialogOpen(false);
    if (!checked || !checkedTree) return;
    onSelect({
      // The tree's own label leaves the document's type to the heading above it; lifted into a
      // form field it has to name the document on its own, so take the canonical name.
      label: checked.userFriendlyName || checked.title,
      documentId: checked.id,
      documentType: checkedTree.type,
    });
    // Left unchecked, so reopening the browser does not start with the last pick still selected.
    setDocumentTrees((trees) =>
      trees.map((tree) => ({
        ...tree,
        documents: tree.documents.map((document) => ({ ...document, checked: false })),
      })),
    );
    handleClose();
  };

  const takeDriveFile = (fileNodes: FileNode[]) => {
    setFileDialogOpen(false);
    const [fileNode] = fileNodes;
    if (!fileNode) return;
    onSelect({
      label: fileNode.fullKey.split('/').pop() || fileNode.fullKey,
      fileNodeKey: fileNode.fullKey,
      fileNodeOwnerId: fileNode.ownerId || undefined,
    });
    handleClose();
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
        <DialogTitle disableTypography className={classes.titleContainer}>
          <Typography className={classes.title}>Browse CENTERLINE</Typography>
          <IconButton className={classes.closeButton} onClick={handleClose}>
            <HighlightOffRounded />
          </IconButton>
        </DialogTitle>
        <DialogContent
          style={{ display: 'flex', flexDirection: 'column', padding: 16, rowGap: 12 }}
        >
          <Button
            variant="outlined"
            color="primary"
            fullWidth
            startIcon={<Description />}
            onClick={openDocumentDialog}
          >
            Browse CENTERLINE Documents
          </Button>
          <Button
            variant="outlined"
            color="primary"
            fullWidth
            startIcon={<AttachFile />}
            onClick={() => setFileDialogOpen(true)}
          >
            Browse CENTERLINE Drive Files
          </Button>
        </DialogContent>
      </Dialog>

      {/* No documentId: Done must not write the pick to a document's associations, only report it. */}
      <AssociateDocumentDialog
        open={documentDialogOpen}
        handleClose={() => setDocumentDialogOpen(false)}
        documents={isLoadingTrees ? [] : documentTrees}
        setDocuments={setDocumentTrees}
        title="Attach Document"
        subtitle="Pick the document this path points at. The field links to the document's page."
        singleSelect
        groupByPhase
        // No showStorytellingPhase: a storytelling form is not a document and has no page of the
        // kind a path field links to. Share Anything offers them because a share sends each one's
        // public link, which is a different thing from a file path.
        onComplete={takeCheckedDocument}
      />

      <AddDesignFileDialog
        open={fileDialogOpen}
        handleClose={() => setFileDialogOpen(false)}
        title="CENTERLINE Drive"
        singleFile
        allowOnlyPdfs
        handleDesignAttach={takeDriveFile}
      />
    </>
  );
}
